import { useEffect, useRef, useState } from 'react';
import { aiRequest } from '@/lib/clinicApi';

export const textLines = text => text.split('\n').map(l => l.trim()).filter(Boolean);
export const useDietParser = (clientId, enabled) => {
  const [text, setText] = useState('');
  const [cache, setCache] = useState({});
  const cacheRef = useRef({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const promise = useRef(null);
  const queued = useRef(null);
  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const commit = (snapshot = text) => {
    if (!clientId || !enabled) return Promise.resolve();
    queued.current = snapshot;
    if (promise.current) return promise.current;
    controller.current = new AbortController();
    setBusy(true); setError('');
    const work = async () => {
      while (queued.current !== null) {
        const current = queued.current; queued.current = null;
        const missing = [...new Set(textLines(current))].filter(l => !cacheRef.current[l]);
        for (let i = 0; i < missing.length; i += 35) {
          const response = await aiRequest(`/clients/${clientId}/parse`, { lines: missing.slice(i, i + 35) }, null, controller.current.signal);
          const additions = Object.fromEntries(response.lines.map(line => [line.line, line]));
          cacheRef.current = { ...cacheRef.current, ...additions }; setCache(cacheRef.current);
        }
      }
    };
    promise.current = work().catch(e => { if (e.name !== 'AbortError') setError(e.message); throw e; }).finally(() => { setBusy(false); promise.current = null; });
    return promise.current;
  };
  const change = next => {
    const newLine = next.split('\n').length > text.split('\n').length;
    setText(next);
    if (newLine) commit(next).catch(() => {});
  };
  const restore = (value, lines) => {
    setText(value); cacheRef.current = Object.fromEntries(lines.map(line => [line.line, line])); setCache(cacheRef.current); setError('');
  };
  const lines = textLines(text).map(l => cache[l]).filter(Boolean);
  const pending = textLines(text).filter(l => !cache[l]).length;
  const totals = { protein: 0, carbs: 0, fat: 0, calories: 0 };
  lines.forEach(line => ['protein', 'carbs', 'fat'].forEach(k => { totals[k] += line[k]; }));
  totals.calories = totals.protein * 4 + totals.carbs * 4 + totals.fat * 9;
  return { text, change, restore, commit, lines, pending, totals, busy, error };
};